import { type DateValue, parseDate } from '@ark-ui/react/date-picker';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EXTERNAL_PRESENCE_APP_URL } from '../config/externalLinks';
import { useSession } from '../contexts';
import { useCloseWeek, useWeekClosePreview } from '../hooks/useCloseWeek';
import type { EmployeeLoggedStatus } from '../data/databaseSchema';
import {
  type ProjectLogEntry,
  timeEntryService,
  timeEntryServiceUtils,
} from '../services/timeEntryService';
import { colors } from '../theme/colors';
import type { EmployeeHours, WeeklySummary } from '../types';
import { getCalendarWeek } from '../utils/dateUtils';
import { AbsenceVacationDialog } from './AbsenceVacationDialog';
import { Calendar } from './Calendar';
import { EmployeeLoggedHoursBox } from './EmployeeLoggedHoursBox';
import { ProjectLoggedHours } from './ProjectLoggedHours';
import { type StepItem, StepsComponent } from './Steps';
import { WeeklySummary as WeeklySummaryComponent } from './WeeklySummary';
import { ZmChangeBanner } from './ZmChangeBanner';

export interface MonthlyData {
  month: number;
  year: number;
}

const TimeTracking: React.FC = () => {
  const { employeeId } = useSession();
  const { closeWeek, closingWeek } = useCloseWeek(employeeId ?? undefined);
  const queryClient = useQueryClient();
  const [monthlyData, setMonthlyData] = useState<MonthlyData>({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [weekStatus, setWeekStatus] = useState<string>('open');

  // Store saved navigation in parent to persist across Calendar remounts
  const [savedNavigation, setSavedNavigation] = useState<DateValue | null>(null);
  const [isAbsenceVacationDialogOpen, setIsAbsenceVacationDialogOpen] = useState(false);
  const isDialogNavigationRef = useRef<boolean>(false);
  const lastDialogMonthRef = useRef<{ month: number; year: number } | null>(null);
  const pendingDialogMonthRef = useRef<{ month: number; year: number } | null>(null);

  const steps = useMemo<StepItem[]>(() => {
    const baseSteps: StepItem[] = [
      { value: '1', title: 'Logged Hours' },
      { value: '2', title: 'Logged Project Hours' },
      { value: '3', title: 'Week Submission' },
    ];

    if (weekStatus === 'reopen') {
      return [...baseSteps, { value: '4', title: 'Week Reopen' }];
    }

    return baseSteps;
  }, [weekStatus]);

  const [employeeHours, setEmployeeHours] = useState<EmployeeHours>({
    isRecorded: false,
    startTime: '',
    endTime: '',
    pauseMinutes: 0,
    plannedHours: 0,
    status: 'open',
  });

  const loggedHours = useMemo(() => {
    return employeeHours.startTime && employeeHours.endTime
      ? (new Date(`2000-01-01T${employeeHours.endTime}`).getTime() -
          new Date(`2000-01-01T${employeeHours.startTime}`).getTime()) /
          (1000 * 60 * 60) -
          employeeHours.pauseMinutes / 60
      : 0;
  }, [employeeHours.startTime, employeeHours.endTime, employeeHours.pauseMinutes]);

  const actualHours = loggedHours;
  const plannedHours = useMemo(() => employeeHours.plannedHours || 0, [employeeHours.plannedHours]);

  const selectedDateString = useMemo(() => {
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    return dateString;
  }, [selectedDate]);

  // Store project entries per date to preserve unsaved changes when switching dates
  const [projectEntriesByDate, setProjectEntriesByDate] = useState<
    Record<string, ProjectLogEntry[]>
  >({});

  // Get project entries for current date
  const projectEntries = useMemo(() => {
    return projectEntriesByDate[selectedDateString] || [];
  }, [projectEntriesByDate, selectedDateString]);

  const currentStep = useMemo(() => {
    const totalProjectHours = projectEntries.reduce((sum, entry) => sum + entry.hours_logged, 0);

    if (weekStatus === 'close' || weekStatus === 'reopen') {
      return 3;
    }
    if (employeeHours.isRecorded && loggedHours === totalProjectHours) {
      return 2;
    }
    if (employeeHours.isRecorded) {
      return 1;
    } else {
      return 0;
    }
  }, [employeeHours.isRecorded, projectEntries, loggedHours, weekStatus]);

  const [monthlyStatus, setMonthlyStatus] = useState<EmployeeLoggedStatus[]>([]);
  const [weekRecord, setWeekRecord] = useState<Record<string, string>>({});
  const [weeklyData, setWeeklyData] = useState<WeeklySummary[]>([]);
  const projectLoggedHours = useMemo(() => {
    return projectEntries.reduce((sum, entry) => sum + entry.hours_logged, 0);
  }, [projectEntries]);

  const calendarWeek = useMemo(() => {
    return getCalendarWeek(selectedDate);
  }, [selectedDate]);
  const weekNo = calendarWeek.split(' ')[1] ?? calendarWeek;
  const isWeekClosed = weekStatus === 'close';

  const isAbsenceOrVacationOrBridge = useMemo(() => {
    const statusForDate = monthlyStatus.find(
      (status) => status.date_of_year === selectedDateString
    );
    return statusForDate
      ? statusForDate.is_absence || statusForDate.is_vacation || statusForDate.is_bridge
      : false;
  }, [monthlyStatus, selectedDateString]);

  const isBridge = useMemo(() => {
    const statusForDate = monthlyStatus.find(
      (status) => status.date_of_year === selectedDateString
    );
    return statusForDate ? (statusForDate.is_bridge ?? false) : false;
  }, [monthlyStatus, selectedDateString]);

  const period = useMemo(() => {
    return `${monthlyData.year}-${String(monthlyData.month).padStart(2, '0')}`;
  }, [monthlyData]);

  const {
    data: timeEntryData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['timeEntries', calendarWeek, employeeId],
    queryFn: () => timeEntryService.getTimeEntries(selectedDateString, employeeId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    enabled: !!employeeId, // Only run query when employeeId is available
  });

  const {
    data: monthlyStatusData,
    refetch: refetchMonthlyStatus,
    isLoading: isMonthlyLoading,
    error: monthlyError,
    dataUpdatedAt: monthlyStatusUpdatedAt,
  } = useQuery({
    queryKey: ['monthlyStatus', period, employeeId],
    queryFn: () => timeEntryService.getTimeEntriesStatus(employeeId, period),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    enabled: !!employeeId, // Only run query when employeeId is available
  });

  const {
    isLoading: isPreviewLoading,
    hasMismatch,
    mismatchReason,
  } = useWeekClosePreview(employeeId ?? undefined, calendarWeek, monthlyStatusUpdatedAt);
  const closeDisabled = isWeekClosed || closingWeek !== null || isPreviewLoading || hasMismatch;

  useEffect(() => {
    if (timeEntryData?.data?.status) {
      setWeekStatus(timeEntryData.data.status);
    }
  }, [timeEntryData, calendarWeek]);

  useEffect(() => {
    if (monthlyStatusData?.data?.week && calendarWeek) {
      const weekStatusFromMonthly = monthlyStatusData.data.week[calendarWeek];
      if (weekStatusFromMonthly) {
        setWeekStatus(weekStatusFromMonthly);
      }
    }
  }, [monthlyStatusData, calendarWeek]);

  // Update employee hours and project entries when data changes
  useEffect(() => {
    if (timeEntryData?.data?.days) {
      const dayDetail = timeEntryData.data.days[selectedDateString];

      if (dayDetail) {
        // Update employee hours from API data
        setEmployeeHours((prev) => ({
          ...prev,
          isRecorded: dayDetail.id !== '00000000-0000-0000-0000-000000000000',
          id: dayDetail.id,
          startTime: dayDetail.start_time || '',
          endTime: dayDetail.end_time || '',
          pauseMinutes: dayDetail.pause || 0,
          plannedHours: dayDetail.planned_hours || 0,
          status: dayDetail.status || 'open',
        }));

        // Only update project entries from API if we don't have unsaved changes for this date
        // This preserves local changes when switching dates
        setProjectEntriesByDate((prev) => {
          if (prev[selectedDateString] && prev[selectedDateString].length > 0) {
            return prev;
          }

          if (dayDetail.project_log && dayDetail.project_log.length > 0) {
            const convertedEntries: ProjectLogEntry[] = dayDetail.project_log.map((entry) => ({
              id: String(entry.id),
              project_info: entry.project_info,
              order_info: entry.order_info,
              task_info: entry.task_info,
              hours_logged: entry.hours_logged,
              description: entry.description,
              work_type: entry.work_type,
              billable: entry.billable ?? entry.task_info?.billable ?? false,
            }));
            return { ...prev, [selectedDateString]: convertedEntries };
          } else {
            return { ...prev, [selectedDateString]: [] };
          }
        });
      } else {
        // Clear data when no entry exists for this date
        setEmployeeHours((prev) => ({
          ...prev,
          isRecorded: false,
          id: '',
          startTime: '',
          endTime: '',
          pauseMinutes: 0,
        }));

        // Only clear project entries if we don't have unsaved changes
        setProjectEntriesByDate((prev) => {
          if (prev[selectedDateString] && prev[selectedDateString].length > 0) {
            return prev;
          }
          return { ...prev, [selectedDateString]: [] };
        });
      }
    } else {
      // Only clear project entries if we don't have unsaved changes
      setProjectEntriesByDate((prev) => {
        if (prev[selectedDateString] && prev[selectedDateString].length > 0) {
          return prev;
        }
        return { ...prev, [selectedDateString]: [] };
      });
    }
  }, [timeEntryData, selectedDateString, employeeId, calendarWeek]);

  useEffect(() => {
    if (timeEntryData?.data?.days) {
      const generateWeeklyData = (date: Date): WeeklySummary[] => {
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay() + 1); // Monday

        const weekData: WeeklySummary[] = [];
        for (let i = 0; i < 5; i++) {
          const currentDate = addDays(startOfWeek, i);
          const dateString = format(currentDate, 'yyyy-MM-dd');

          const dateData = {
            date: dateString,
            fteHours: 0,
            projectLoggedHours: 0,
            actualHours: 0,
            difference: 0,
          };

          const dayDetail = timeEntryData.data.days[dateString];

          if (dateString === selectedDateString) {
            if (projectEntries.length > 0) {
              dateData.projectLoggedHours = projectEntries.reduce(
                (sum, entry) => sum + entry.hours_logged,
                0
              );
            } else {
              dateData.projectLoggedHours = 0;
            }
          } else {
            dateData.projectLoggedHours =
              dayDetail?.project_log?.reduce((sum, entry) => sum + entry.hours_logged, 0) || 0;
          }

          const fteHours = dayDetail?.planned_hours || 0;
          let actualHours = 0;

          if (dateString === selectedDateString) {
            actualHours = timeEntryServiceUtils.calculateHoursBooked(
              employeeHours.startTime,
              employeeHours.endTime,
              employeeHours.pauseMinutes || 0
            );
          } else {
            actualHours = dayDetail
              ? timeEntryServiceUtils.calculateHoursBooked(
                  dayDetail.start_time || '',
                  dayDetail.end_time || '',
                  dayDetail.pause || 0
                )
              : 0;
          }

          dateData.actualHours = actualHours;
          dateData.fteHours = fteHours;

          dateData.difference = actualHours - fteHours;

          weekData.push(dateData);
        }
        return weekData;
      };

      const weeklyData = generateWeeklyData(selectedDate);

      setWeeklyData(weeklyData);
    }
  }, [timeEntryData, selectedDateString, employeeId, calendarWeek, projectEntries, employeeHours]);

  useEffect(() => {
    if (monthlyStatusData?.data) {
      setWeekRecord(monthlyStatusData.data.week || {});

      const statusData = monthlyStatusData.data.day;
      if (statusData?.length > 0) {
        const enhancedStatus = statusData.map((status) => ({
          ...status,
          daily_hours_booked: status.daily_hours_booked ?? 0,
          total_project_hours_booked: status.total_project_hours_booked ?? 0,
          is_holiday: status.is_holiday ?? false,
          is_absence: status.is_absence ?? false,
          is_vacation: status.is_vacation ?? false,
          is_bridge: status.is_bridge ?? false,
        }));
        setMonthlyStatus(enhancedStatus);
      } else {
        setMonthlyStatus([]);
      }
    }
  }, [monthlyStatusData]);

  useEffect(() => {
    if (pendingDialogMonthRef.current && savedNavigation) {
      const pending = pendingDialogMonthRef.current;
      const navMonth = savedNavigation.month;
      const navYear = savedNavigation.year;
      if (pending.month === navMonth && pending.year === navYear) {
        setMonthlyData({ month: pending.month, year: pending.year });
        pendingDialogMonthRef.current = null;
      }
    }
  }, [savedNavigation]);

  // Sync monthlyStatus when logged hours or project hours change for the selected date
  useEffect(() => {
    if (monthlyStatus.length > 0 && selectedDateString) {
      // Calculate actual hours from employeeHours for selected date
      const actualHours =
        employeeHours.startTime && employeeHours.endTime
          ? timeEntryServiceUtils.calculateHoursBooked(
              employeeHours.startTime,
              employeeHours.endTime,
              employeeHours.pauseMinutes || 0
            )
          : 0;

      // Calculate total project hours from projectEntries for selected date
      const totalProjectHours = projectEntries.reduce((sum, entry) => sum + entry.hours_logged, 0);

      // Update monthlyStatus for the selected date
      setMonthlyStatus((prev) => {
        const currentStatus = prev.find((s) => s.date_of_year === selectedDateString);
        if (!currentStatus) return prev;

        // Only update if values have changed to avoid unnecessary re-renders
        if (
          currentStatus.daily_hours_booked === actualHours &&
          currentStatus.total_project_hours_booked === totalProjectHours
        ) {
          return prev;
        }

        return prev.map((status) => {
          if (status.date_of_year === selectedDateString) {
            return {
              ...status,
              daily_hours_booked: actualHours,
              total_project_hours_booked: totalProjectHours,
              // Update status if hours are entered (change from NotLogged to draft)
              status:
                actualHours > 0 || totalProjectHours > 0
                  ? status.status === 'NotLogged'
                    ? 'draft'
                    : status.status
                  : status.status,
            };
          }
          return status;
        });
      });
    }
  }, [
    employeeHours.startTime,
    employeeHours.endTime,
    employeeHours.pauseMinutes,
    projectEntries,
    selectedDateString,
  ]);

  // Calculate hours booked based on start time, end time, and pause
  const calculateHoursBooked = (
    startTime: string,
    endTime: string,
    pauseMinutes: number
  ): number => {
    const start = new Date(`${selectedDateString}T${startTime}`);
    const end = new Date(`${selectedDateString}T${endTime}`);
    const totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const workingMinutes = totalMinutes - pauseMinutes;
    return Math.max(0, workingMinutes / 60);
  };

  // Update hours booked when inputs change
  useEffect(() => {
    const hoursBooked = calculateHoursBooked(
      employeeHours.startTime,
      employeeHours.endTime,
      employeeHours.pauseMinutes
    );

    const projectHours = projectEntries.reduce((sum, entry) => sum + entry.hours_logged, 0);

    setEmployeeHours((prev) => ({
      ...prev,
      hoursBooked,
      projectLoggedHours: projectHours,
    }));
  }, [employeeHours.startTime, employeeHours.endTime, employeeHours.pauseMinutes, projectEntries]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);

    const newMonth = date.getMonth() + 1;
    const newYear = date.getFullYear();
    setMonthlyData((prev) => {
      if (prev.month !== newMonth || prev.year !== newYear) {
        return { month: newMonth, year: newYear };
      }
      return prev;
    });
  };

  const handleMonthChange = useCallback(
    (month: number, year: number) => {
      if (isDialogNavigationRef.current) {
        return;
      }

      if (
        lastDialogMonthRef.current &&
        lastDialogMonthRef.current.month === month &&
        lastDialogMonthRef.current.year === year
      ) {
        return;
      }

      if (
        lastDialogMonthRef.current &&
        monthlyData.month === lastDialogMonthRef.current.month &&
        monthlyData.year === lastDialogMonthRef.current.year &&
        month !== lastDialogMonthRef.current.month
      ) {
        return;
      }

      const selectedDateMonth = selectedDate.getMonth() + 1;
      const selectedDateYear = selectedDate.getFullYear();

      // Skip if it's a reset to selected date's month and monthlyData already matches
      if (
        month === selectedDateMonth &&
        year === selectedDateYear &&
        monthlyData.month === month &&
        monthlyData.year === year
      ) {
        return;
      }

      setMonthlyData((prev) => {
        const willUpdate = prev.month !== month || prev.year !== year;
        if (willUpdate) {
          return { month, year };
        }
        return prev;
      });
    },
    [monthlyData, selectedDate]
  );

  const handleEmployeeHoursChange = (updatedHours: Partial<EmployeeHours>) => {
    setEmployeeHours((prev) => ({ ...prev, ...updatedHours }));
  };

  const handleSaveLoggedHours = async () => {
    if (!employeeId || !selectedDate) return;

    try {
      const dateOfYear = selectedDate.toISOString();

      const combineDateTime = (date: Date, timeString: string): string => {
        if (!timeString) return date.toISOString();
        const sanitized = timeString.replace(/[^0-9:]/g, '');
        if (!sanitized) return date.toISOString();
        const [hours, minutes] = sanitized.split(':').map(Number);
        const combinedDate = new Date(date);
        combinedDate.setUTCHours(hours, minutes || 0, 0, 0);
        return combinedDate.toISOString();
      };

      const startTime = combineDateTime(selectedDate, employeeHours.startTime);
      const endTime = combineDateTime(selectedDate, employeeHours.endTime);

      if (employeeHours.isRecorded) {
        await timeEntryService.updateLoggedHours(employeeHours.id || '', {
          employee_id: employeeId,
          start_time: startTime,
          end_time: endTime,
          pause_hours: employeeHours.pauseMinutes / 60,
        });
      } else {
        await timeEntryService.createLoggedHours({
          employee_id: employeeId,
          date_of_year: dateOfYear,
          start_time: startTime,
          end_time: endTime,
          pause_hours: employeeHours.pauseMinutes / 60,
        });
      }

      // Invalidate and refetch time entries
      await queryClient.invalidateQueries({ queryKey: ['timeEntries', calendarWeek, employeeId] });
      await refetch();
      await refetchMonthlyStatus();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteLoggedHours = async () => {
    if (!employeeId) return;

    try {
      // Clear employee hours state
      setEmployeeHours((prev) => ({
        ...prev,
        isRecorded: false,
        id: undefined,
        startTime: '',
        endTime: '',
        pauseMinutes: 0,
      }));

      // Invalidate and refetch time entries
      await queryClient.invalidateQueries({ queryKey: ['timeEntries', calendarWeek, employeeId] });
      await refetch();
      await refetchMonthlyStatus();
    } catch (error) {
      throw error;
    }
  };

  const handleProjectEntriesChange = (entries: ProjectLogEntry[]) => {
    // Update project entries for the current date
    setProjectEntriesByDate((prev) => ({
      ...prev,
      [selectedDateString]: entries,
    }));
  };

  const handleProjectEntrySaved = () => {
    // Clear the cached entries for this date so they reload from API
    setProjectEntriesByDate((prev) => {
      const updated = { ...prev };
      delete updated[selectedDateString];
      return updated;
    });
    // Trigger refetch of time entries data after successful submission
    refetch();
    refetchMonthlyStatus();
  };

  const canSubmit = useMemo(() => projectEntries.length > 0, [projectEntries]);

  // Error state removed - handled in ProjectLoggedHours
  if (error || monthlyError) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: colors.neutral.grey[50],
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
            color: colors.accent.red,
            textAlign: 'center',
            padding: '2rem',
          }}
        >
          <p>
            Error loading time entries: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <button
            onClick={() => refetch()}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: colors.accent.blue,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.neutral.grey[50],
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <ZmChangeBanner employeeId={employeeId} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingRight: '1rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <StepsComponent
            items={steps}
            currentStep={currentStep}
            scrollTargets={{
              0: 'logged-hours-section',
              1: 'project-hours-section',
              2: 'week-submission-section',
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => closeWeek(calendarWeek)}
          disabled={closeDisabled}
          title={!isWeekClosed ? mismatchReason : undefined}
          style={{
            flexShrink: 0,
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: colors.neutral.white,
            backgroundColor:
              closeDisabled ? colors.neutral.grey[400] : colors.accent.blue,
            cursor: closingWeek !== null ? 'wait' : closeDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          {closingWeek === calendarWeek
            ? 'Closing…'
            : isWeekClosed
              ? `Week ${weekNo} Closed`
              : `Close Week ${weekNo}`}
        </button>
      </div>

      <div style={{ padding: '0.5rem', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Top Section */}
        <div
          id="logged-hours-section"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          <div style={{ position: 'relative' }}>
            {isMonthlyLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '8px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '1rem',
                    color: colors.neutral.grey[600],
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      border: `4px solid ${colors.neutral.grey[200]}`,
                      borderTop: `4px solid ${colors.accent.blue}`,
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                  <p>Loading time entries...</p>
                </div>
              </div>
            )}
            <Calendar
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              monthlyStatus={monthlyStatus}
              onMonthChange={handleMonthChange}
              weekRecord={weekRecord}
              savedNavigation={savedNavigation}
              onSaveNavigation={setSavedNavigation}
              onOpenAbsenceVacationDialog={() => setIsAbsenceVacationDialogOpen(true)}
              onOpenExternalPresence={() =>
                window.open(EXTERNAL_PRESENCE_APP_URL, '_blank', 'noopener,noreferrer')
              }
            />
          </div>
          <EmployeeLoggedHoursBox
            employeeHours={employeeHours}
            projectLoggedHours={projectLoggedHours}
            onChange={handleEmployeeHoursChange}
            selectedDate={selectedDate}
            onSave={handleSaveLoggedHours}
            onDelete={handleDeleteLoggedHours}
            disabled={weekStatus === 'close' || isAbsenceOrVacationOrBridge}
            isAbsenceOrVacation={isAbsenceOrVacationOrBridge}
          />
        </div>

        <div id="project-hours-section">
          <ProjectLoggedHours
            selectedDate={selectedDate}
            entries={projectEntries}
            onChange={handleProjectEntriesChange}
            onEntrySaved={handleProjectEntrySaved}
            canSubmit={canSubmit}
            dataLoading={isLoading}
            employeeLoggedHours={loggedHours}
            actualHours={actualHours}
            plannedHours={plannedHours}
            disabled={weekStatus === 'close' || isAbsenceOrVacationOrBridge}
            isAbsenceOrVacation={isAbsenceOrVacationOrBridge}
          />
        </div>

        {/* Weekly Summary Section */}
        <div id="week-submission-section">
          <WeeklySummaryComponent
            data={weeklyData}
            isLoading={isLoading}
            employeeId={employeeId}
            calendarWeek={calendarWeek}
            weekStatus={weekStatus}
            closeBlocked={isPreviewLoading || hasMismatch}
            closeBlockedReason={mismatchReason}
          />
        </div>
      </div>

      <AbsenceVacationDialog
        isOpen={isAbsenceVacationDialogOpen}
        onClose={() => setIsAbsenceVacationDialogOpen(false)}
        currentMonth={monthlyData}
        onMonthChange={(month, year) => {
          isDialogNavigationRef.current = true;
          lastDialogMonthRef.current = { month, year };
          pendingDialogMonthRef.current = { month, year };
          const newDateValue = parseDate(new Date(year, month - 1, 1));
          setSavedNavigation(newDateValue);
          setTimeout(() => {
            isDialogNavigationRef.current = false;
            setTimeout(() => {
              lastDialogMonthRef.current = null;
              pendingDialogMonthRef.current = null;
            }, 500);
          }, 300);
        }}
        onSuccess={(updatedDates) => {
          if (updatedDates && updatedDates.length > 0) {
            setProjectEntriesByDate((prev) => {
              const updated = { ...prev };
              updatedDates.forEach((date) => {
                delete updated[date];
              });
              return updated;
            });
          }

          queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
          queryClient.invalidateQueries({ queryKey: ['monthlyStatus'] });
        }}
        monthlyStatus={monthlyStatus}
      />
    </div>
  );
};

export default TimeTracking;
